import { BooleanCell } from '@folio/components/module-elements/PolicyTable/BooleanCell';
import { DateCell } from '@folio/components/module-elements/PolicyTable/DateCell';
import { ProductCell } from '@folio/components/module-elements/PolicyTable/ProductCell';
import { StatusCell } from '@folio/components/module-elements/PolicyTable/StatusCell';
import { StringCell } from '@folio/components/module-elements/PolicyTable/StringCell';
import { Button } from '@folio/components/ui/button';
import { TableCell, TableRow } from '@folio/components/ui/table';
import type { DerogationWithPolicyAndCustomerEntity } from 'korint-sdk';
import type { DerogationColumnConfig } from 'korint-sdk/react';
import _ from 'lodash';
import { EyeIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

const getColumnValue = (
  column: DerogationColumnConfig,
  data: DerogationWithPolicyAndCustomerEntity,
): string | number | boolean | Date | undefined | null => {
  if ('path' in column && column.path) {
    return _.get(data, column.path);
  }

  if ('accessor' in column && column.accessor) {
    return column.accessor(data);
  }

  return undefined;
};

const getPolicyLink = (policyId: string, branchId?: string) => {
  return branchId ? `/policies/${policyId}/${branchId}/events` : `/policies/${policyId}/events`;
};

interface DerogationRowProps {
  derogationEntity: DerogationWithPolicyAndCustomerEntity;
  columns: Array<DerogationColumnConfig>;
}

export function DerogationRow({ derogationEntity, columns }: DerogationRowProps) {
  const { t } = useTranslation();
  return (
    <TableRow key={derogationEntity.branch?.branchId ?? derogationEntity.policy.id}>
      {columns.map(column => {
        const value = getColumnValue(column, derogationEntity);

        if (value === undefined && column.type !== 'boolean') {
          return (
            <TableCell key={column.name}>
              <div className="italic text-muted-foreground">{t('policyTable.unavailable')}</div>
            </TableCell>
          );
        }

        switch (column.type) {
          case 'string': {
            return (
              <TableCell key={column.name}>
                {typeof value === 'string' ? <StringCell value={value} /> : null}
              </TableCell>
            );
          }
          case 'boolean': {
            return (
              <TableCell key={column.name}>
                {typeof value === 'boolean' ? <BooleanCell value={value} /> : null}
              </TableCell>
            );
          }
          case 'status': {
            return (
              <TableCell key={column.name} data-testid="status">
                {typeof value === 'string' ? (
                  <StatusCell
                    value={value}
                    statuses={column.statuses}
                    translationNamespace={column.translationNamespace}
                  />
                ) : null}
              </TableCell>
            );
          }
          case 'date': {
            const { format } = column;

            return (
              <TableCell key={column.name}>
                {typeof value === 'string' ? (
                  <DateCell value={new Date(value)} format={format} />
                ) : null}
              </TableCell>
            );
          }
          case 'product': {
            return (
              <TableCell key={column.name}>
                {typeof value === 'string' ? <ProductCell productId={value} /> : null}
              </TableCell>
            );
          }
        }
      })}

      <TableCell>
        <Link to={getPolicyLink(derogationEntity.policy.id, derogationEntity.branch?.branchId)}>
          <Button variant="ghost" size="sm">
            {t('policyTable.view')}
            <EyeIcon className="ml-2 h-4 w-4" />
          </Button>
        </Link>
      </TableCell>
    </TableRow>
  );
}
